import { getAgents } from "./queryFunctions";
import { listProviders, getUserDetailsAPI, listSkills, installSkill, updateSkill, removeSkill, previewSkill, fetchSkillMd, saveSkillCredential } from "./providers";
import { listSchedulerJobs, createSchedulerJob, updateSchedulerJob, deleteSchedulerJob, listAdminSchedulerJobs } from "./schedulerApi";
import { getFinOpsSummary, getAuditLog } from "./adminFinOps";
import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";
import { STALE_NEVER, STALE_30S, STALE_5MIN, CACHE_FOREVER } from "../constants/const";

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import QUERYKEYS, { chatModelInfoQueryKey } from "./queryKeys";
import UserAuthStore from "../store/userStore";
import {
  addCollaborator,
  createSpectra,
  deleteMessage,
  deleteSpectra,
  leaveSpectra,
  editSpectra,
  getAIModels,
  getFileData,
  getUserTags,
  getSpectraActivities,
  getSpectraSparks,
  getSpectraById,
  getSpectraOwner,
  getSpectraDetails,
  moveChatAPI,
  getChatMessages,
  qzAgentBuild,
  qzAgentAtomCreationAPI,
  removeCollaborator,
  setChatModel,
  updateSparkTitle,
  qzAgentBuildDetailsAPI,
  qzAgentGetTools,
  getWorkspaceDetails,
  deleteAllSparks,
  initAnalysisMessage,
  listAgents,
} from "./queryFunctions";
import { getChatModelInfo } from "../utils/sparkApi/getChatModelInfo";

import {
  getModelPreferences,
  updateModelPreferences,
  toggleModelEnabled,
  setDefaultModel,
  updateModelPreference,
  getModelDefaults,
  updateModelDefaults,
  getAllModels,
  updateModel,
  updateModelOrder,
  createModel,
  deleteModel,
  exportMessage,
  getModelProvider,
  updateModelProvider,
  deleteModelProvider,
  listModelProviders,
} from "./queryFunctions";

// React Query hook for GET_AGENTS
export const useGetAgents = (data, config = {}) =>
  useQuery({
    queryKey: ["get-agents", data],
    queryFn: () => getAgents(data),
    enabled: !!data?.org_id,
    staleTime: STALE_5MIN,
    ...config,
  });

// React Query hook for GET_PROVIDERS
export const useGetProviders = (orgId, config = {}) =>
  useQuery({
    queryKey: ["get-providers", orgId],
    queryFn: () => listProviders(orgId),
    enabled: !!orgId,
    staleTime: STALE_5MIN,
    ...config,
  });

// User Tags Hook
export const useUserTags = (orgId, userId, config = {}) =>
  useQuery({
    queryKey: ["user-tags", orgId, userId],
    queryFn: () => getUserTags(orgId, userId),
    enabled: !!orgId && !!userId,
    staleTime: STALE_5MIN,
    ...config,
  });

// Spectra Activities Hook
export const useSpectraActivities = (orgId, spaceId, limit = 20, offset = 0, config = {}) =>
  useQuery({
    queryKey: ["spectra-activities", orgId, spaceId, limit, offset],
    queryFn: () => getSpectraActivities(orgId, spaceId, limit, offset),
    enabled: !!orgId && !!spaceId,
    staleTime: STALE_30S,
    ...config,
  });

// Get Sparks (Personal Chats) Hook
export const useGetSparks = (orgId, userId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.CHATS, orgId, userId],
    queryFn: async () => {
      const response = await api({
        method: "GET",
        url: `/spark/get_sparks/${orgId}/${userId}`,
      });
      const chats = response?.data?.chats;
      return Array.isArray(chats) ? chats : [];
    },
    enabled: !!orgId && !!userId,
    staleTime: STALE_30S,
    ...config,
  });

// Get Spectras (Spaces) Hook
export const useGetSpectras = (orgId, userId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.SPECTRAS, orgId, userId],
    queryFn: async () => {
      const response = await api({
        method: "GET",
        url: `/spectra/get_spectra/${orgId}/${userId}`,
      });
      return response?.data?.spaces || [];
    },
    enabled: !!orgId && !!userId,
    staleTime: STALE_30S,
    ...config,
  });

// Spectra Sparks Hook
export const useSpectraSparks = (spaceId, orgId, userId, ownerId, config = {}) =>
  useQuery({
    queryKey: ["spectra-sparks", spaceId, orgId, userId, ownerId],
    queryFn: () => getSpectraSparks(spaceId, orgId, userId, ownerId),
    enabled: !!spaceId && !!orgId && !!userId,
    staleTime: STALE_30S,
    ...config,
  });

// Single Spectra Hook
export const useSpectraById = (spaceId, orgId, userId, config = {}) =>
  useQuery({
    queryKey: ["spectra-by-id", spaceId, orgId, userId],
    queryFn: () => getSpectraById(spaceId, orgId, userId),
    enabled: !!spaceId && !!orgId && !!userId,
    staleTime: STALE_30S,
    ...config,
  });

// Lightweight hook: fetches only owner_id for a space — unblocks chat queries
// without waiting for the full space document (which includes large file_toc arrays).
export const useSpectraOwner = (spaceId, orgId, userId, config = {}) =>
  useQuery({
    queryKey: ["spectra-owner", spaceId, orgId, userId],
    queryFn: () => getSpectraOwner(spaceId, orgId, userId),
    enabled: !!spaceId && !!orgId && !!userId,
    staleTime: STALE_30S,
    ...config,
  });

// Comprehensive Spectra Details Hook (replaces useSpectraById, useSpectraSparks, useSpectraActivities)
export const useSpectraDetailsQuery = (spaceId, orgId, userId, ownerId, params = {}, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.SPECTRA_DETAILS, spaceId, orgId, userId, ownerId, params],
    queryFn: () => getSpectraDetails(spaceId, orgId, userId, ownerId, params),
    enabled: !!spaceId && !!orgId && !!userId, // ownerId is optional, backend derives it
    staleTime: STALE_30S,
    ...config,
  });

// Agent Triggers Hook
const BUILT_IN_CAPABILITY_TRIGGERS = ["@lepton"];

export const useAgentTriggers = (orgId, config = {}) =>
  useQuery({
    queryKey: ["agent-triggers", orgId],
    queryFn: async () => {
      const data = await listAgents({ org_id: orgId });
      const agentTriggers = data?.agents?.flatMap(agent => agent.triggers || []) || [];
      return [...new Set([...agentTriggers, ...BUILT_IN_CAPABILITY_TRIGGERS])];
    },
    enabled: !!orgId,
    staleTime: STALE_5MIN,
    ...config,
  });
  
// Model Preferences Hooks
export const useGetModelPreferences = (orgId, userId, config = {}) =>
  useQuery({
    queryKey: ["model-preferences", orgId, userId],
    queryFn: () => getModelPreferences(orgId, userId),
    enabled: !!orgId && !!userId,
    staleTime: STALE_5MIN,
    ...config,
  });

export const useUpdateModelPreferences = (config = {}) =>
  useMutation({
    mutationFn: ({ orgId, userId, models }) => updateModelPreferences(orgId, userId, models),
    ...config,
  });

export const useToggleModelEnabled = (config = {}) =>
  useMutation({
    mutationFn: ({ orgId, userId, modelId, enabled }) => toggleModelEnabled(orgId, userId, modelId, enabled),
    ...config,
  });

export const useSetDefaultModel = (config = {}) =>
  useMutation({
    mutationFn: ({ orgId, userId, modelId }) => setDefaultModel(orgId, userId, modelId),
    ...config,
  });

export const useUpdateModelPreference = (config = {}) =>
  useMutation({
    mutationFn: ({ orgId, userId, modelId, updates }) => updateModelPreference(orgId, userId, modelId, updates),
    ...config,
  });

export const useGetModelDefaults = (orgId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.GET_MODEL_DEFAULTS, orgId],
    queryFn: () => getModelDefaults(orgId),
    enabled: !!orgId,
    staleTime: STALE_5MIN,
    ...config,
  });

export const useUpdateModelDefaults = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, userId, defaults }) => updateModelDefaults(orgId, userId, defaults),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODEL_DEFAULTS, orgId] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODELS] });
    },
    ...config,
  });
};

export const useGetAllModels = (config = {}) => {
  const orgId = UserAuthStore((state) => state.orgId);
  return useQuery({
    queryKey: [QUERYKEYS.GET_ALL_MODELS, orgId],
    queryFn: () => getAllModels(orgId),
    enabled: !!orgId,
    staleTime: STALE_NEVER,
    refetchOnMount: true,
    ...config,
  });
};

export const useUpdateModel = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERYKEYS.UPDATE_MODEL],
    mutationFn: ({ orgId, userId, modelId, updates, replace }) => updateModel(orgId, userId, modelId, updates, replace),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_ALL_MODELS, orgId] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODELS, orgId] });
    },
    ...config,
  });
};

export const useUpdateModelOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, userId, models }) => updateModelOrder(orgId, userId, models),
    onSettled: (_, __, { orgId }) => Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_ALL_MODELS, orgId] }),
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODELS, orgId] }),
    ]),
  });
};

export const useCreateModel = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERYKEYS.CREATE_MODEL],
    // modelData must already have UPPERCASE keys before calling
    mutationFn: ({ orgId, userId, modelId, modelData }) => createModel(orgId, userId, modelId, modelData),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_ALL_MODELS, orgId] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODELS, orgId] });
    },
    ...config,
  });
};

export const useDeleteModel = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERYKEYS.DELETE_MODEL],
    mutationFn: ({ orgId, userId, modelId }) => deleteModel(orgId, userId, modelId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_ALL_MODELS, orgId] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODELS, orgId] });
    },
    ...config,
  });
};

export const useGetAIModels = (config = {}) => {
  const orgId = UserAuthStore((state) => state.orgId);
  return useQuery({
    queryKey: [QUERYKEYS.GET_MODELS, orgId],
    queryFn: getAIModels,
    enabled: !!orgId,          // don't fire until orgId is known
    staleTime: STALE_NEVER,    // always re-fetch in background on mount
    refetchOnMount: true,
    refetchOnWindowFocus: true, // re-fetch when user returns to tab
    ...config,
  });
};

// Desktop Bridge — list all connected devices for the current user across all client_ids.
// Powers the global Prompt-bar device selector (no clientId required).
export const useUserDesktopDevices = (config = {}) => {
  const { enabled = true, ...queryConfig } = config;
  const capability = useQuery({
    queryKey: ["desktop-bridge-capability"],
    queryFn: () =>
      api({ method: "GET", url: ENDPOINTS.DESKTOP_CAPABILITIES }).then(
        (r) => r.data.desktop_bridge_enabled === true
      ),
    staleTime: STALE_5MIN,
    refetchOnWindowFocus: true,
  });

  return useQuery({
    queryKey: ["desktop-user-devices"],
    queryFn: () =>
      api({ method: "GET", url: ENDPOINTS.DESKTOP_USER_DEVICES }).then(
        (r) => r.data.devices || []
      ),
    staleTime: STALE_30S,
    refetchOnWindowFocus: true,
    // refetchInterval removed (Phase G5): DEVICE_CONNECTED / DEVICE_DISCONNECTED
    // SSE events update the cache instantly. refetchOnWindowFocus covers the gap
    // if the SSE stream was down while a device changed state.
    ...queryConfig,
    enabled: enabled && capability.data === true,
  });
};

// Desktop Bridge — durable device history from Firestore (Phase G2).
// Returns all devices ever paired, including offline and revoked.
// No polling — history only changes on first pair (G1 upsert) or explicit revoke (G3 mutation).
// Revoke mutation invalidates this key + ["desktop-user-devices"] on success.
export const useUserDeviceHistory = (config = {}) =>
  useQuery({
    queryKey: ["desktop-user-device-history"],
    queryFn: () =>
      api({ method: "GET", url: ENDPOINTS.DESKTOP_USER_DEVICE_HISTORY }).then(
        (r) => r.data.devices || []
      ),
    staleTime: STALE_5MIN,
    refetchOnWindowFocus: true,
    ...config,
  });

// Desktop Bridge — revoke a device (Phase G3).
// Optimistically flips status in the history cache, then confirms from server.
// Also invalidates live presence so the prompt-bar chip clears if the device was online.
export const useRevokeDevice = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, deviceId }) =>
      api({ method: "POST", url: ENDPOINTS.DESKTOP_REVOKE_DEVICE(clientId, deviceId) }),
    onMutate: async ({ deviceId }) => {
      await queryClient.cancelQueries({ queryKey: ["desktop-user-device-history"] });
      const previous = queryClient.getQueryData(["desktop-user-device-history"]);
      queryClient.setQueryData(["desktop-user-device-history"], (old = []) =>
        old.map((d) => d.device_id === deviceId ? { ...d, status: "revoked" } : d)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["desktop-user-device-history"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["desktop-user-device-history"] });
      queryClient.invalidateQueries({ queryKey: ["desktop-user-devices"] });
    },
    ...config,
  });
};

// Desktop Bridge — initiate device enrollment OTP email (Phase 6).
// Sends a 6-digit OTP to the authenticated user's email. Pass clientId if
// known (e.g. from existing device history) to bind the invite to a client.
export const useInitDeviceEnrollment = (config = {}) =>
  useMutation({
    mutationFn: (clientId) =>
      api({
        method: "POST",
        url: ENDPOINTS.DESKTOP_ENROLL_INIT,
        data: clientId ? { client_id: clientId } : {},
      }).then((r) => r.data),
    ...config,
  });

// ─── Model Provider Config hooks ──────────────────────────────────────────────

export const useGetModelProvider = (orgId, modelProviderId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.GET_MODEL_PROVIDER, orgId, modelProviderId],
    queryFn: () => getModelProvider(orgId, modelProviderId),
    enabled: !!orgId && !!modelProviderId,
    staleTime: STALE_5MIN,
    ...config,
  });

export const useUpdateModelProvider = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, userId, modelProviderId, data }) =>
      updateModelProvider(orgId, userId, modelProviderId, data),
    onSuccess: (_, { orgId, modelProviderId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODEL_PROVIDER, orgId, modelProviderId] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.LIST_MODEL_PROVIDERS, orgId] });
    },
    ...config,
  });
};

export const useDeleteModelProvider = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, modelProviderId }) =>
      deleteModelProvider(orgId, modelProviderId),
    onSuccess: (_, { orgId, modelProviderId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.GET_MODEL_PROVIDER, orgId, modelProviderId] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.LIST_MODEL_PROVIDERS, orgId] });
    },
    ...config,
  });
};

export const useListModelProviders = (orgId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.LIST_MODEL_PROVIDERS, orgId],
    queryFn: () => listModelProviders(orgId),
    enabled: !!orgId,
    staleTime: STALE_5MIN,
    ...config,
  });

export const useCreateSpectraMutation = (config = {}) => {
  const queryClient = useQueryClient();
  // `onSuccess`/`onError` are pulled out and explicitly merged below instead
  // of relying on `...config` spread last — spreading `config` after literal
  // onSuccess/onError keys means a caller-supplied handler REPLACES the base
  // handler entirely (object spread always overwrites earlier keys), not
  // "in addition to" it. That silently broke two things whenever a caller
  // (e.g. CreateSpectra.js) passed its own onSuccess/onError:
  //   - onSuccess's invalidateQueries calls (SPECTRA_DETAILS/SPECTRAS/
  //     spectra-by-id) never ran, so the UI relied solely on the optimistic
  //     entry from onMutate (missing server-computed fields like model_name)
  //     until some unrelated refetch happened to occur.
  //   - onError's rollback of that optimistic entry never ran either, so a
  //     failed create left a permanent ghost Spectra in the sidebar/dashboard
  //     that doesn't actually exist on the backend, with no error shown.
  const { onSuccess: callerOnSuccess, onError: callerOnError, ...restConfig } = config;

  return useMutation({
    mutationFn: createSpectra,
    mutationKey: [QUERYKEYS.CREATE_SPECTRA],
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.SPECTRAS] });

      // Snapshot all matching spectras queries for rollback
      const previousQueries = queryClient.getQueriesData({ queryKey: [QUERYKEYS.SPECTRAS] });

      // Build an optimistic space entry matching the shape from get_spectra
      const optimisticSpace = {
        id: variables.sid,
        space_id: variables.sid,
        title: variables.title,
        tag: variables.tag,
        emoji: variables.emoji,
        description: variables.description || "",
        status: "CREATING",
        owner_id: variables.uid,
        collaborators: [{ id: variables.uid, user_id: variables.uid, status: "ACCEPTED" }],
        chat_ids: [],
        model_id: variables.model_id,
        files: variables.files || [],
        system_prompt: variables.system_prompt || "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "owned",
      };

      // Prepend to ALL spectras queries (each has different [orgId, userId] key)
      queryClient.setQueriesData({ queryKey: [QUERYKEYS.SPECTRAS] }, (old) => {
        if (!Array.isArray(old)) return old;
        return [optimisticSpace, ...old];
      });

      return { previousQueries };
    },
    onSuccess: (data, variables, context) => {
      // Refetch to get the real server data (timestamps, model_name, etc.)
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      queryClient.invalidateQueries({ queryKey: ["spectra-by-id"] });

      callerOnSuccess?.(data, variables, context);
    },
    onError: (err, variables, context) => {
      // Rollback optimistic update on failure
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      callerOnError?.(err, variables, context);
    },
    ...restConfig,
  });
};


export const useDeleteSpectraMutation = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteSpectra,
    mutationKey: [QUERYKEYS.DELETE_SPECTRA],
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.SPECTRAS] });

      // Snapshot for rollback
      const previousQueries = queryClient.getQueriesData({ queryKey: [QUERYKEYS.SPECTRAS] });

      // Optimistically remove from ALL spectras queries
      queryClient.setQueriesData({ queryKey: [QUERYKEYS.SPECTRAS] }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.filter(s => s.space_id !== variables.sid);
      });

      return { previousQueries };
    },
    onSuccess: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      queryClient.invalidateQueries({ queryKey: ["spectra-by-id"] });
    },
    onError: (err, variables, context) => {
      // Rollback on failure
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    ...config,
  });
};

export const useLeaveSpectraMutation = (config = {}) =>
  useMutation({
    mutationFn: leaveSpectra,
    mutationKey: [QUERYKEYS.LEAVE_SPECTRA],
    ...config,
  });

export const useEditSpectraMutation = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: editSpectra,
    mutationKey: [QUERYKEYS.EDIT_SPECTRA],
    onSuccess: () => {
      // Invalidate spectra queries to reflect changes
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      queryClient.invalidateQueries({ queryKey: ["spectra-by-id"] });
    },
    ...config,
  });
};

export const useMoveChatAPI = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: moveChatAPI,
    mutationKey: [QUERYKEYS.MOVE_CHAT],
    onMutate: async (variables) => {
      const { new_sid, old_sid, chat_ids, chat_data, org_id, uid } = variables;
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.CHATS] });
      await queryClient.cancelQueries({ queryKey: ["spectra-sparks"] });
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      
      // Snapshot previous values for rollback
      const previousPersonalChats = queryClient.getQueryData([QUERYKEYS.CHATS, org_id, uid]);
      const previousSourceSpectraSparks = old_sid 
        ? queryClient.getQueriesData({ queryKey: ["spectra-sparks", old_sid] })
        : null;
      const previousDestSpectraSparks = new_sid
        ? queryClient.getQueriesData({ queryKey: ["spectra-sparks", new_sid] })
        : null;
      // Snapshot the spectra LIST (holds chat_ids, which drives the sidebar (N) badge)
      const previousSpectrasList = queryClient.getQueriesData({ queryKey: [QUERYKEYS.SPECTRAS] });

      // Keep the spectra list's chat_ids in sync so the (N) count badge updates
      // instantly. Entry shape matches the backend: { user_id, chat_id }.
      const movedEntries = chat_ids.map((cid) => ({ user_id: uid, chat_id: cid }));
      queryClient.setQueriesData({ queryKey: [QUERYKEYS.SPECTRAS] }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((space) => {
          if (new_sid && space.space_id === new_sid) {
            const existing = Array.isArray(space.chat_ids) ? space.chat_ids : [];
            const toAdd = movedEntries.filter(
              (e) => !existing.some((x) => x?.chat_id === e.chat_id)
            );
            return toAdd.length ? { ...space, chat_ids: [...existing, ...toAdd] } : space;
          }
          if (old_sid && space.space_id === old_sid) {
            const existing = Array.isArray(space.chat_ids) ? space.chat_ids : [];
            return { ...space, chat_ids: existing.filter((x) => !chat_ids.includes(x?.chat_id)) };
          }
          return space;
        });
      });
      
      // Personal -> Spectra: remove from personal chats, add to destination spectra
      if (!old_sid && new_sid) {
        queryClient.setQueryData([QUERYKEYS.CHATS, org_id, uid], (old) => {
          if (!old) return old;
          return old.filter(chat => !chat_ids.includes(chat.uid));
        });
        if (chat_data) {
          queryClient.setQueriesData(
            { queryKey: ["spectra-sparks", new_sid], exact: false },
            (old) => {
              const sparks = old?.sparks ?? [];
              const incoming = chat_data.filter(c => !sparks.some(s => s.uid === c.uid));
              if (incoming.length === 0) return old ?? { success: true, sparks: chat_data };
              return { ...(old ?? { success: true }), sparks: [...sparks, ...incoming] };
            }
          );
        }
      }

      // Spectra -> Spectra: add to destination spectra (source removal handled below)
      if (old_sid && new_sid && chat_data) {
        queryClient.setQueriesData(
          { queryKey: ["spectra-sparks", new_sid], exact: false },
          (old) => {
            const sparks = old?.sparks ?? [];
            const incoming = chat_data.filter(c => !sparks.some(s => s.uid === c.uid));
            if (incoming.length === 0) return old ?? { success: true, sparks: chat_data };
            return { ...(old ?? { success: true }), sparks: [...sparks, ...incoming] };
          }
        );
      }
      
      // Optimistically remove from source spectra
      if (old_sid) {
        queryClient.setQueriesData(
          { queryKey: ["spectra-sparks", old_sid], exact: false },
          (old) => {
            if (!old?.sparks) return old;
            return {
              ...old,
              sparks: old.sparks.filter(spark => !chat_ids.includes(spark.uid))
            };
          }
        );
      }
      
      return { previousPersonalChats, previousSourceSpectraSparks, previousDestSpectraSparks, previousSpectrasList };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      const { org_id, uid, old_sid, new_sid, chat_ids } = variables;
      
      if (context?.previousPersonalChats) {
        queryClient.setQueryData([QUERYKEYS.CHATS, org_id, uid], context.previousPersonalChats);
      }
      if (context?.previousSourceSpectraSparks) {
        context.previousSourceSpectraSparks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousDestSpectraSparks) {
        context.previousDestSpectraSparks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousSpectrasList) {
        context.previousSpectrasList.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('useMoveChatAPI', 'Failed to move spark', { err: err?.message, org_id, uid, old_sid, new_sid, chat_ids });
    },
    onSettled: (data, error, variables) => {
      // Sync with server after mutation completes
      const { org_id, uid, old_sid, new_sid } = variables;
      
      // For personal chats, mark stale without immediate refetch to preserve optimistic update
      // The stale data will be refetched on next window focus or navigation
      queryClient.invalidateQueries({ 
        queryKey: [QUERYKEYS.CHATS, org_id, uid],
        refetchType: 'none'  // Mark stale but don't refetch immediately
      });
      
      // Invalidate source spectra
      if (old_sid) {
        queryClient.invalidateQueries({ queryKey: ["spectra-sparks", old_sid] });
      }
      
      // Invalidate destination spectra
      if (new_sid) {
        queryClient.invalidateQueries({ queryKey: ["spectra-sparks", new_sid] });
      }
      
      // Refetch the spectra list so chat_ids (and the (N) badge) reconcile with the server
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
    },
    ...config,
  });
};

export const useUpdateSparkTitle = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: updateSparkTitle,
    mutationKey: [QUERYKEYS.UPDATE_SPARK_TITLE],
    onMutate: async (variables) => {
      const queryKey = chatModelInfoQueryKey(variables.chat_id, variables.space_id);
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData(queryKey);
      
      // Optimistically update title
      queryClient.setQueryData(queryKey, (old) =>
        old ? { ...old, title: variables.title } : old
      );
      
      return { previousData, queryKey };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      logger.error('useUpdateSparkTitle', 'Failed to update spark title', { err: err?.message, chat_id: variables.chat_id });
    },
    onSettled: (data, error, variables) => {
      // Refetch to sync with server
      queryClient.invalidateQueries({
        queryKey: chatModelInfoQueryKey(variables.chat_id, variables.space_id),
      });
    },
    ...config,
  });
};

export const useAddCollaboratorMutation = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: addCollaborator,
    mutationKey: [QUERYKEYS.ADD_COLLABORATOR],
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData([QUERYKEYS.SPECTRA_DETAILS, variables.space_id]);
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData([QUERYKEYS.SPECTRA_DETAILS, variables.space_id], context.previousData);
      }
      logger.error('useAddCollaboratorMutation', 'Failed to add collaborator', { err: err?.message, space_id: variables.space_id });
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      queryClient.invalidateQueries({ queryKey: ["spectra-by-id"] });
    },
    ...config,
  });
};

export const useRemoveCollaboratorMutation = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: removeCollaborator,
    mutationKey: [QUERYKEYS.REMOVE_COLLABORATOR],
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData([QUERYKEYS.SPECTRA_DETAILS, variables.space_id]);
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData([QUERYKEYS.SPECTRA_DETAILS, variables.space_id], context.previousData);
      }
      logger.error('useRemoveCollaboratorMutation', 'Failed to remove collaborator', { err: err?.message, space_id: variables.space_id });
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      queryClient.invalidateQueries({ queryKey: ["spectra-by-id"] });
    },
    ...config,
  });
};

export const useSetChatModel = (config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: setChatModel,
    mutationKey: [QUERYKEYS.SET_CHAT_MODEL],
    onMutate: async (variables) => {
      const queryKey = chatModelInfoQueryKey(variables.chat_id, variables.space_id);
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot the previous value
      const previousChatData = queryClient.getQueryData(queryKey);
      
      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          model_id: variables.model_id,
          persisted_model_id: variables.model_id,
        };
      });
      
      // Return context with the snapshot
      return { previousChatData, queryKey };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousChatData) {
        queryClient.setQueryData(context.queryKey, context.previousChatData);
      }
      logger.error('useSetChatModel', 'Failed to set chat model', { err: err?.message, chat_id: variables.chat_id, model_id: variables.model_id });
    },
    onSettled: (data, error, variables) => {
      // Refetch to sync with server
      queryClient.invalidateQueries({
        queryKey: chatModelInfoQueryKey(variables.chat_id, variables.space_id),
      });
    },
    ...config,
  });
};

export const useChatInfo = (chat_id, space, config = {}) =>
  useQuery({
    queryKey: chatModelInfoQueryKey(chat_id, space?.space_id),
    queryFn: () => getChatModelInfo(chat_id, space),
    refetchOnWindowFocus: false,
    retry: 1,
    ...config,
  });

export const useChatMessages = (chat_id, space, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space?.space_id],
    queryFn: () => getChatMessages(chat_id, space),
    staleTime: STALE_NEVER,    // always re-fetch; SSE + manual invalidation are the update paths
    gcTime: CACHE_FOREVER,     // keep in memory until manually invalidated (v5: gcTime, not cacheTime)
    refetchOnWindowFocus: false,  // Don't refetch on tab focus
    retry: 2,
    enabled: !!chat_id,  // Only fetch if chat_id exists
    ...config,
  });

/**
 * Helper to update chat messages cache.
 * Used by mutations to update messages without local state.
 */
export const updateChatMessagesCache = (queryClient, chatId, spaceId, updater) => {
  queryClient.setQueryData(
    [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId],
    (oldData) => {
      const currentData = oldData || { messages: [] };
      return {
        ...currentData,
        messages: updater(currentData.messages || [])
      };
    }
  );
};

const appendOptimisticAttempt = (message, {
  answerId,
  promptId,
  modelId,
  timestamp,
  assetIds = [],
  attachments = [],
  promptVariant,
}) => {
  const answers = message.answers || [];
  return {
    ...message,
    approval_id: undefined,
    approval_status: undefined,
    approval_status_at: undefined,
    answers: [
      ...answers,
      {
        id: answerId,
        content: "",
        timestamp,
        asset_ids: assetIds,
        attachments,
        role: "model",
        model_id: modelId,
        prompt_id: promptId,
        adk_session_id: null,
        ...(promptVariant && { prompt_variant: promptVariant }),
      },
    ],
    currentIndex: answers.length,
  };
};

/**
 * Delete message mutation with optimistic updates.
 * Deletes a message from chat and updates cache optimistically.
 */
export const useDeleteMessage = (chatId, spaceId, config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ delete_following, ...data }) => deleteMessage(data),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId] });
      
      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId]);
      
      // Optimistically update — remove just the version if answer_id provided, else remove whole message
      updateChatMessagesCache(queryClient, chatId, spaceId, (messages) => {
        const promptIndex = messages.findIndex((message) =>
          message.id === variables.prompt_id ||
          message.answers?.some((answer) =>
            answer.id === variables.answer_id && answer.prompt_id === variables.prompt_id
          )
        );
        const visibleMessages = variables.delete_following && promptIndex >= 0
          ? messages.slice(0, promptIndex + 1)
          : messages;
        if (!variables.answer_id) {
          return visibleMessages.filter((message) => message.id !== variables.prompt_id);
        }
        return visibleMessages.reduce((acc, message) => {
          const containsTarget = message.id === variables.prompt_id || message.answers?.some(
            (answer) => answer.id === variables.answer_id && answer.prompt_id === variables.prompt_id
          );
          if (!containsTarget) {
            acc.push(message);
            return acc;
          }
          const updatedAnswers = (message.answers || []).filter(a => a.id !== variables.answer_id);
          if (updatedAnswers.length === 0) return acc;
          acc.push({
            ...message,
            answers: updatedAnswers,
            currentIndex: Math.min(message.currentIndex || 0, updatedAnswers.length - 1),
          });
          return acc;
        }, []);
      });

      // If no messages remain, optimistically remove spark from sidebar
      const updated = queryClient.getQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId]);
      if (updated?.messages?.length === 0) {
        const { user, orgId } = UserAuthStore.getState();
        if (orgId && user?.uid) {
          if (spaceId && variables.owner_id) {
            // Spectra sidebar — instant removal
            queryClient.setQueryData(['spectra-sparks', spaceId, orgId, user.uid, variables.owner_id], (old) => {
              if (!old?.sparks) return old;
              return { ...old, sparks: old.sparks.filter(s => s.uid !== chatId) };
            });
          } else {
            // Private spark sidebar
            queryClient.setQueryData([QUERYKEYS.CHATS, orgId, user.uid], (old) => {
              if (!old || !Array.isArray(old)) return old;
              return old.filter(c => c.uid !== chatId);
            });
          }
        }
      }

      // Return context with snapshot for rollback
      return { previousMessages };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], context.previousMessages);
      }
      logger.error('useDeleteMessage', 'Failed to delete message', { err: err?.message, chatId, spaceId, prompt_id: variables.prompt_id });
    },
    onSuccess: () => {
      // Invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId] });
    },
    ...config,
  });
};

/**
 * Edit message mutation with optimistic updates.
 * Updates message content and creates new answer.
 */
export const useEditMessage = (chatId, spaceId, config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ messageId, newPrompt, answerId, modelId }) => {
      // This is optimistic - the actual API call happens in handleEditSend
      // We're just updating the cache here
      return Promise.resolve({ messageId, newPrompt, answerId, modelId });
    },
    onMutate: async (variables) => {
      const {
        messageId,
        promptId,
        newPrompt,
        answerId,
        modelId,
        userDetails,
      } = variables;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId] });
      
      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId]);
      
      const timestamp = new Date().toISOString();

      // Optimistically append the immutable edit attempt to the existing turn.
      updateChatMessagesCache(queryClient, chatId, spaceId, (messages) =>
        messages.map((message) => {
          if (message.id === messageId) {
            const assetIds = message.asset_ids || [];
            const attachments = message.attachments || [];
            return appendOptimisticAttempt(message, {
              answerId,
              promptId,
              modelId,
              timestamp,
              assetIds,
              attachments,
              promptVariant: {
                prompt_id: promptId,
                content: newPrompt,
                timestamp,
                asset_ids: assetIds,
                attachments,
                user_details: userDetails,
              },
            });
          }
          return message;
        })
      );
      
      // Return context with snapshot for rollback
      return { previousMessages };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], context.previousMessages);
      }
      logger.error('useEditMessage', 'Failed to edit message', { err: err?.message, chatId, spaceId, messageId: variables.messageId });
    },
    ...config,
  });
};

/**
 * Update message approval status mutation
 * Updates approval fields for a message
 */
export const useUpdateMessageApproval = (chatId, spaceId, config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ messageId, approvalId, status }) => {
      // This is optimistic - actual approval happens elsewhere
      return Promise.resolve({ messageId, approvalId, status });
    },
    onMutate: async (variables) => {
      const { messageId, approvalId, status } = variables;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId] });
      
      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId]);
      
      // Optimistically update the message
      const timestamp = new Date().toISOString();
      updateChatMessagesCache(queryClient, chatId, spaceId, (messages) => {
        // Try to find by exact ID first
        let targetMessage = messages.find(m => m.id === messageId);
        
        // If not found, use the last user message as fallback
        if (!targetMessage) {
          for (let i = messages.length - 1; i >= 0; i--) {
            if (!messages[i].role || messages[i].role === 'user') {
              targetMessage = messages[i];
              break;
            }
          }
        }
        
        if (!targetMessage) {
          logger.warn('useUpdateMessageApproval', 'No suitable message found for approval update', { messageId });
          return messages;
        }
        
        return messages.map((message) => {
          if (message.id === targetMessage.id) {
            return {
              ...message,
              approval_id: approvalId,
              approval_status: status,
              approval_status_at: timestamp,
            };
          }
          return message;
        });
      });
      
      // Return context with snapshot for rollback
      return { previousMessages };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], context.previousMessages);
      }
      logger.error('useUpdateMessageApproval', 'Failed to update message approval', { err: err?.message, chatId, spaceId, messageId: variables.messageId, approvalId: variables.approvalId });
    },
    ...config,
  });
};

/**
 * Send message mutation.
 * Creates optimistic message + empty answer, returns promptId for streaming.
 */
export const useSendMessage = (chatId, spaceId, config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ promptId }) => {
      // This is optimistic - actual streaming handled separately
      // Just return the promptId for tracking
      return Promise.resolve({ promptId });
    },
    onMutate: async (variables) => {
      const { promptId, prompt, assetIds, attachments, userDetails } = variables;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId] });
      
      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId]);
      
      // Optimistically add the new message.
      // model_id starts null — patched by MODEL_USED SSE event when streaming starts.
      updateChatMessagesCache(queryClient, chatId, spaceId, (messages) => [
        ...messages,
        {
          role: "user",
          content: prompt,
          answers: [
            {
              content: "",
              timestamp: new Date().toISOString(),
              asset_ids: [],
              attachments: [],
              role: "model",
              model_id: null,
              adk_session_id: null,
            },
          ],
          currentIndex: 0,
          asset_ids: assetIds || [],
          attachments: attachments || [],
          user_details: userDetails,
          id: promptId,
          timestamp: new Date().toISOString(),
          expanded: null,
        },
      ]);
      
      // Return context with snapshot for rollback
      return { previousMessages };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], context.previousMessages);
      }
      logger.error('useSendMessage', 'Failed to send message', { err: err?.message, chatId, spaceId, promptId: variables.promptId });
    },
    ...config,
  });
};

/**
 * Regenerate message mutation.
 * Adds new empty answer to existing message for regeneration.
 */
export const useRegenerateMessage = (chatId, spaceId, config = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ answerId }) => {
      // This is optimistic - actual streaming handled separately
      return Promise.resolve({ answerId });
    },
    onMutate: async (variables) => {
      const { messageId, promptId, answerId, modelId } = variables;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, spaceId] });
      
      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId]);
      
      // Optimistically add new answer to the message
      updateChatMessagesCache(queryClient, chatId, spaceId, (messages) =>
        messages.map((message) => {
          if (message.id === messageId) {
            return appendOptimisticAttempt(message, {
              answerId,
              promptId,
              modelId,
              timestamp: new Date().toISOString(),
            });
          }
          return message;
        })
      );
      
      // Return context with snapshot for rollback
      return { previousMessages };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], context.previousMessages);
      }
      logger.error('useRegenerateMessage', 'Failed to regenerate message', { err: err?.message, chatId, spaceId, messageId: variables.messageId, answerId: variables.answerId });
    },
    ...config,
  });
};

export const useGetFileData = (mutate, config = {}) =>
  useMutation({
    mutationFn: getFileData,
    mutationKey: [QUERYKEYS.GET_FILE_DATA],
    ...config,
  });

export const useQzAgentBuild = (config = {}) =>
  useMutation({
    mutationFn: qzAgentBuild,
    mutationKey: [QUERYKEYS.QUANTUM_AGENT_BUILD],
    ...config,
  });

export const useQzAgentAtomCreationAPI = (config = {}) =>
  useMutation({
    mutationFn: qzAgentAtomCreationAPI,
    mutationKey: [QUERYKEYS.QUANTUM_AGENT_CALL],
    ...config,
  });

export const useQzAgentBuildDetailsAPI = (config = {}) =>
  useMutation({
    mutationFn: qzAgentBuildDetailsAPI,
    mutationKey: [QUERYKEYS.QUANTUM_AGENT_BUILD_DETAILS],
    ...config,
  });

export const useQzAgentGetTools = (config = {}) =>
  useMutation({
    mutationFn: qzAgentGetTools,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    mutationKey: [QUERYKEYS.QUANTUM_AGENT_GET_TOOLS],
    ...config,
  });

export const useGetWorkspaceDetails = (config = {}) =>
  useMutation({
    mutationFn: getWorkspaceDetails,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    mutationKey: [QUERYKEYS.GET_WORKSPACE_DETAILS],
    ...config,
  });

export const useDeleteAllSparksMutation = (config = {}) =>
  useMutation({
    mutationFn: deleteAllSparks,
    mutationKey: [QUERYKEYS.DELETE_ALL_SPARKS],
    ...config,
  });

export const useInitAnalysisMessageMutation = (config = {}) =>
  useMutation({
    mutationFn: initAnalysisMessage,
    mutationKey: [QUERYKEYS.INITANALYSISMESSAGE],
    ...config,
  });

export const useListAgents = (data, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.LIST_AGENTS, data],
    queryFn: () => listAgents(data),
    enabled: !!data?.org_id,
    ...config,
  });

// ================= User Details Hook (No Cache) =================

export const useGetUserDetails = (orgId, userId, config = {}) =>
  useQuery({
    queryKey: ['user-details', userId, orgId],
    queryFn: () => getUserDetailsAPI(userId, orgId),
    enabled: !!orgId && !!userId,
    staleTime: STALE_NEVER,
    gcTime: STALE_NEVER,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...config,
  });

/**
 * Hook to export a message to PDF or DOCX.
 * Returns a download URL which the caller should trigger as a browser download.
 */
export const useExportMessage = (config = {}) =>
  useMutation({
    mutationFn: exportMessage,
    onError: (err) => {
      logger.error('useExportMessage', 'Failed to export message', { error: err?.message });
    },
    ...config,
  });

// =============================================================================
// SCHEDULER HOOKS
// =============================================================================

/**
 * List all scheduled jobs for the current user.
 */
export const useListSchedulerJobs = (config = {}) => {
  const user = UserAuthStore((state) => state.user);
  return useQuery({
    queryKey: [QUERYKEYS.LIST_SCHEDULER_JOBS, user?.uid],
    queryFn: listSchedulerJobs,
    enabled: !!user?.uid,
    staleTime: STALE_NEVER,
    ...config,
  });
};

/**
 * Create a new scheduled prompt job.
 */
export const useCreateSchedulerJob = (config = {}) => {
  const queryClient = useQueryClient();
  const user = UserAuthStore.getState().user;
  const { onSuccess: callerOnSuccess, ...restConfig } = config;
  return useMutation({
    mutationFn: createSchedulerJob,
    mutationKey: [QUERYKEYS.CREATE_SCHEDULER_JOB],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.LIST_SCHEDULER_JOBS, user?.uid] });
      callerOnSuccess?.(data, variables, context);
    },
    ...restConfig,
  });
};

/**
 * Update an existing scheduled job (toggle enabled, change cadence, etc.).
 */
export const useUpdateSchedulerJob = (config = {}) => {
  const queryClient = useQueryClient();
  const user = UserAuthStore.getState().user;
  const { onSuccess: callerOnSuccess, ...restConfig } = config;
  return useMutation({
    mutationFn: updateSchedulerJob,
    mutationKey: [QUERYKEYS.UPDATE_SCHEDULER_JOB],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.LIST_SCHEDULER_JOBS, user?.uid] });
      callerOnSuccess?.(data, variables, context);
    },
    ...restConfig,
  });
};

/**
 * Delete a scheduled job.
 */
export const useDeleteSchedulerJob = (config = {}) => {
  const queryClient = useQueryClient();
  const user = UserAuthStore.getState().user;
  const { onSuccess: callerOnSuccess, ...restConfig } = config;
  return useMutation({
    mutationFn: deleteSchedulerJob,
    mutationKey: [QUERYKEYS.DELETE_SCHEDULER_JOB],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.LIST_SCHEDULER_JOBS, user?.uid] });
      callerOnSuccess?.(data, variables, context);
    },
    ...restConfig,
  });
};

/**
 * List all scheduled jobs across the org (admin only).
 */
export const useListAdminSchedulerJobs = (config = {}) => {
  return useQuery({
    queryKey: [QUERYKEYS.LIST_ADMIN_SCHEDULER_JOBS],
    queryFn: listAdminSchedulerJobs,
    staleTime: STALE_NEVER,
    ...config,
  });
};

/**
 * Delete a scheduled job from the admin view — invalidates the admin list cache.
 */
export const useDeleteAdminSchedulerJob = (config = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restConfig } = config;
  return useMutation({
    mutationFn: deleteSchedulerJob,
    mutationKey: [QUERYKEYS.DELETE_SCHEDULER_JOB],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.LIST_ADMIN_SCHEDULER_JOBS] });
      callerOnSuccess?.(data, variables, context);
    },
    ...restConfig,
  });
};

// Skills Hooks

export const useGetSkills = (orgId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.LIST_SKILLS, orgId],
    queryFn: () => listSkills(orgId),
    enabled: !!orgId,
    staleTime: STALE_30S,
    ...config,
  });

export const useGetSkillMd = (orgId, skillId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.GET_SKILL_MD, orgId, skillId],
    queryFn: () => fetchSkillMd(orgId, skillId),
    enabled: !!orgId && !!skillId,
    staleTime: STALE_30S,
    ...config,
  });

export const useInstallSkill = (config = {}) =>
  useMutation({
    mutationKey: [QUERYKEYS.INSTALL_SKILL],
    mutationFn: ({ orgId, uid, command, selectedSkills, skillScopes }) => installSkill(orgId, uid, command, selectedSkills, skillScopes),
    ...config,
  });

export const useUpdateSkill = (config = {}) =>
  useMutation({
    mutationKey: [QUERYKEYS.UPDATE_SKILL],
    mutationFn: ({ orgId, uid, skillId, updates }) => updateSkill(orgId, uid, skillId, updates),
    ...config,
  });

export const useRemoveSkill = (config = {}) =>
  useMutation({
    mutationKey: [QUERYKEYS.REMOVE_SKILL],
    mutationFn: ({ orgId, uid, skillId, force }) => removeSkill(orgId, uid, skillId, force),
    ...config,
  });

export const usePreviewSkill = (config = {}) =>
  useMutation({
    mutationKey: [QUERYKEYS.PREVIEW_SKILL],
    mutationFn: ({ command }) => previewSkill(command),
    ...config,
  });

export const useSaveSkillCredential = (config = {}) =>
  useMutation({
    mutationKey: [QUERYKEYS.SAVE_SKILL_CREDENTIAL],
    mutationFn: ({ orgId, uid, skillId, apiKey }) => saveSkillCredential(orgId, uid, skillId, apiKey),
    ...config,
  });

// Persistent Memory Hooks

export const useGetPersistentMemory = (orgId, userId, chatId, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.GET_PERSISTENT_MEMORY, orgId, userId, chatId],
    queryFn: async () => {
      const response = await api({
        method: "GET",
        url: ENDPOINTS.GET_PERSISTENT_MEMORY(orgId, userId, chatId),
      });
      return response?.data;
    },
    enabled: !!orgId && !!userId && !!chatId,
    staleTime: 0,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    ...config,
  });

export const useSupersedePersistentMemory = (config = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restConfig } = config;
  return useMutation({
    mutationFn: async ({ orgId, userId, chatId, index }) => {
      const response = await api({
        method: "PATCH",
        url: ENDPOINTS.SUPERSEDE_MEMORY_ENTRY(orgId, userId, chatId, index),
      });
      return response?.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QUERYKEYS.GET_PERSISTENT_MEMORY, variables.orgId, variables.userId, variables.chatId],
      });
      callerOnSuccess?.(data, variables, context);
    },
    ...restConfig,
  });
};

// =============================================================================
// Admin FinOps & Audit Log
// =============================================================================

// FinOps usage summary (admin-only)
export const useFinOpsSummary = (params = {}, config = {}) =>
  useQuery({
    queryKey: [QUERYKEYS.ADMIN_FINOPS_SUMMARY, params.period, params.from, params.to],
    queryFn: () => getFinOpsSummary(params),
    staleTime: STALE_30S,
    ...config,
  });

// Audit log events (admin-only) — infinite/cursor-paginated
export const useAuditLog = (params = {}, config = {}) =>
  useInfiniteQuery({
    queryKey: [QUERYKEYS.ADMIN_AUDIT_LOG, params.period, params.from, params.to, params.eventType, params.entityId],
    queryFn: ({ pageParam }) => getAuditLog({ ...params, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: STALE_30S,
    ...config,
  });
